import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";

import {
  BudgetHistoryType,
  DisableBudgetForSubaccountMutation,
  DisableBudgetForSubaccountMutationVariables,
  GetLatestBudgetsQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import {
  DISABLE_BUDGET_FOR_SUBACCOUNT,
  GET_LATEST_BUDGETS,
} from "~frontend/graphql/budgets";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { palette } from "~frontend/theme";
import { formatDate } from "~frontend/util/dateFormat";
import useMutation from "~frontend/util/useMutation";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { BudgetHistoryDialog } from "./BudgetHistoryDialog";
import { EditBudgetDialog } from "./EditBudgetDialog";
import { EnforceBudgetDialog } from "./EnforceBudgetDialog";
import { ResetBudgetDialog } from "./ResetBudgetDialog";
import { Budget, Subaccount, SubaccountWithBudget } from "./types";
import {
  budgetIsActive,
  formatMaybeNegativeMillicentsAsDollarsWithDollarSign,
} from "./util";

function SmallLoadingIndicator() {
  return <Loading size={20} style={{ marginTop: 0 }} />;
}

export function OrganizationBudgets(): ReactElement {
  useBreadcrumbs([{ label: "Budgets", path: "/organization/budgets" }]);

  const tenant = useTenant()!;

  const confirm = useConfirm();

  const { data, loading, error } =
    useQuery<GetLatestBudgetsQuery>(GET_LATEST_BUDGETS);

  const [loadingSubaccountIds, setLoadingSubaccountIds] = useState<Set<string>>(
    new Set()
  );
  const setSubaccountIdLoading = (subaccountId: string) =>
    setLoadingSubaccountIds(new Set([...loadingSubaccountIds, subaccountId]));
  const setSubaccountIdNotLoading = (subaccountId: string) =>
    setLoadingSubaccountIds(
      new Set([...loadingSubaccountIds].filter(si => si !== subaccountId))
    );
  const subaccountIsLoading = (subaccountId: string) =>
    loadingSubaccountIds.has(subaccountId);

  const [
    budgetHistoryDialogOpenForSubaccount,
    setBudgetHistoryDialogOpenForSubaccount,
  ] = useState<(SubaccountWithBudget & { budget: Budget }) | null>(null);

  const [
    enforceBudgetDialogOpenForSubaccount,
    setEnforceBudgetDialogOpenForSubaccount,
  ] = useState<Subaccount | null>(null);

  const [
    editBudgetDialogOpenForSubaccount,
    setEditBudgetDialogOpenForSubaccount,
  ] = useState<(SubaccountWithBudget & { budget: Budget }) | null>(null);

  const [
    resetBudgetDialogOpenForSubaccount,
    setResetBudgetDialogOpenForSubaccount,
  ] = useState<Subaccount | null>(null);

  const [disableBudget] = useMutation<
    DisableBudgetForSubaccountMutation,
    DisableBudgetForSubaccountMutationVariables
  >(DISABLE_BUDGET_FOR_SUBACCOUNT, {
    onErrorSnackbarMessage: "Error disabling budget",
    refetchQueries: [GET_LATEST_BUDGETS],
    awaitRefetchQueries: true,
  });

  async function handleDisableBudget(subaccount: Subaccount) {
    const { confirmed } = await confirm({
      title: (
        <>
          Disable Budget for <strong>{subaccount.name}</strong>
        </>
      ),
      description:
        "Disabling your budget on this subaccount will end the current budgeting period, and stop tracking of your spending against the budget. If you wish to enforce a budget again in the future, you will start a new budgeting period beginning the day that you turn it on.",
      confirmationText: "Disable Budget",
    });

    if (confirmed) {
      setSubaccountIdLoading(subaccount.id);
      await disableBudget({
        variables: { input: { subaccountId: subaccount.id } },
      });
      setSubaccountIdNotLoading(subaccount.id);
    }
  }

  function getActiveBudgetForSubaccount({ budget }: SubaccountWithBudget) {
    if (budget && budgetIsActive(budget)) {
      return budget;
    } else {
      return null;
    }
  }

  function renderLastModified(subaccountWithBudget: SubaccountWithBudget) {
    if (!subaccountWithBudget.budget?.history.length) {
      return "-";
    }

    const lastBudgetModification =
      subaccountWithBudget.budget.history[
        subaccountWithBudget.budget.history.length - 1
      ];
    const secondToLastBudgetModification =
      subaccountWithBudget.budget.history[
        subaccountWithBudget.budget.history.length - 2
      ];
    const budgetAllocation = subaccountWithBudget.budget.allocationInMillicents;
    const budgetAllocationBeforeLastModification =
      secondToLastBudgetModification?.allocationInMillicents ?? null;

    let lastModificationText = "";
    switch (lastBudgetModification.type) {
      case BudgetHistoryType.BudgetCreated:
        lastModificationText = `${lastBudgetModification.createdBy.fullName} turned budget on ${formatDate(lastBudgetModification.createdAt)}`;
        break;
      case BudgetHistoryType.BudgetCreatedByReset:
        lastModificationText = `${lastBudgetModification.createdBy.fullName} reset budget ${formatDate(lastBudgetModification.createdAt)}`;
        break;
      case BudgetHistoryType.BudgetAllocationUpdated:
        if (
          budgetAllocationBeforeLastModification !== null &&
          budgetAllocation > budgetAllocationBeforeLastModification
        ) {
          lastModificationText = `${lastBudgetModification.createdBy.fullName} increased budget ${formatDate(lastBudgetModification.createdAt)}`;
        } else if (
          budgetAllocationBeforeLastModification !== null &&
          budgetAllocation < budgetAllocationBeforeLastModification
        ) {
          lastModificationText = `${lastBudgetModification.createdBy.fullName} decreased budget ${formatDate(lastBudgetModification.createdAt)}`;
        } else {
          lastModificationText = `${lastBudgetModification.createdBy.fullName} updated budget ${formatDate(lastBudgetModification.createdAt)}`;
        }
        break;
      case BudgetHistoryType.BudgetEnded:
        lastModificationText = `${lastBudgetModification.createdBy.fullName} turned budget off off ${formatDate(lastBudgetModification.createdAt)}`;
    }

    return (
      <>
        <Typography variant="body3">{lastModificationText}</Typography>
        <br />
        <LinkButton
          onClick={() =>
            setBudgetHistoryDialogOpenForSubaccount(
              subaccountWithBudget as SubaccountWithBudget & { budget: Budget }
            )
          }
        >
          <Typography variant="body3">View History</Typography>
        </LinkButton>
      </>
    );
  }

  return (
    <PageContainer>
      <PageHeader>Budgets</PageHeader>
      <Typography variant="h2" sx={{ marginBottom: "40px" }}>
        Set a budget for your organization to make sure you don’t go over your
        spending limit. If you exceed your budget, messaging will be paused
        until the budget is increased, or a new budgeting period is started. You
        can edit your budget at any time, or end your current budgeting period
        and reset with a new budget. These restrictions will only apply when
        budgeting is enforced.
      </Typography>
      <Box
        sx={{
          backgroundColor: palette.white,
          paddingX: "40px",
          paddingY: "24px",
        }}
      >
        <AsyncWrapper data={data} loading={loading} errors={error}>
          {data => (
            <Table
              columns={[
                ...(tenant.subaccountsEnabled
                  ? [
                      {
                        key: "subaccount",
                        title: "Subaccount",
                        renderValue: (row: SubaccountWithBudget) =>
                          row.subaccount.name,
                        sx: { verticalAlign: "top" },
                      },
                    ]
                  : []),
                {
                  key: "budget",
                  title: "Budget",
                  renderValue: row => {
                    if (subaccountIsLoading(row.id)) {
                      return <SmallLoadingIndicator />;
                    }
                    const budget = getActiveBudgetForSubaccount(row);
                    if (budget) {
                      return `$${formatMillicentsAsDollars(budget.allocationInMillicents)}`;
                    }
                    return "-";
                  },
                  align: "left",
                  sx: { verticalAlign: "top" },
                },
                {
                  key: "balance",
                  title: "Balance",
                  renderValue: row => {
                    if (subaccountIsLoading(row.id)) {
                      return <SmallLoadingIndicator />;
                    }
                    const budget = getActiveBudgetForSubaccount(row);
                    if (budget) {
                      const balanceInMillicents =
                        budget.allocationInMillicents -
                        budget.usageInMillicents;
                      if (
                        balanceInMillicents <=
                        budget.warningThresholdInMillicents
                      ) {
                        return (
                          <strong style={{ color: palette.red }}>
                            {formatMaybeNegativeMillicentsAsDollarsWithDollarSign(
                              balanceInMillicents
                            )}
                          </strong>
                        );
                      }
                      return formatMaybeNegativeMillicentsAsDollarsWithDollarSign(
                        balanceInMillicents
                      );
                    }
                    return "-";
                  },
                  align: "left",
                  sx: { verticalAlign: "top" },
                },
                {
                  key: "history",
                  title: "Last Modified",
                  renderValue: row => {
                    if (subaccountIsLoading(row.id)) {
                      return <SmallLoadingIndicator />;
                    }
                    return renderLastModified(row);
                  },
                  align: "left",
                  sx: { verticalAlign: "top" },
                },
                {
                  key: "enforced",
                  title: "Budget Enforced",
                  renderValue: row => {
                    if (subaccountIsLoading(row.id)) {
                      return <SmallLoadingIndicator />;
                    }
                    const budgetEnforced = !!getActiveBudgetForSubaccount(row);
                    return (
                      <FormControlLabel
                        label={
                          budgetEnforced ? "Budget Enforced" : "Budget Disabled"
                        }
                        control={
                          <Switch
                            checked={budgetEnforced}
                            onChange={async (_event, checked) => {
                              if (checked) {
                                setEnforceBudgetDialogOpenForSubaccount(
                                  row.subaccount
                                );
                              } else {
                                await handleDisableBudget(row.subaccount);
                              }
                            }}
                          />
                        }
                      />
                    );
                  },
                  align: "left",
                  sx: { verticalAlign: "top" },
                },
                {
                  key: "actions",
                  title: "",
                  renderValue: row => {
                    if (subaccountIsLoading(row.id)) {
                      return <SmallLoadingIndicator />;
                    }

                    return getActiveBudgetForSubaccount(row) ? (
                      <FlexBox direction="column" gap="6px">
                        <Button
                          variant="contained"
                          color="tertiary"
                          onClick={() =>
                            setEditBudgetDialogOpenForSubaccount(
                              row as SubaccountWithBudget & { budget: Budget }
                            )
                          }
                          sx={{
                            fontSize: "10px",
                            padding: "6px 8px",
                            height: "auto",
                          }}
                        >
                          Edit Budget
                        </Button>
                        <LinkButton
                          onClick={() =>
                            setResetBudgetDialogOpenForSubaccount(
                              row.subaccount
                            )
                          }
                        >
                          Reset Budget
                        </LinkButton>
                      </FlexBox>
                    ) : (
                      <></>
                    );
                  },
                  sx: { verticalAlign: "top" },
                },
              ]}
              rows={data.getLatestBudgets}
              noFooter
            />
          )}
        </AsyncWrapper>
      </Box>
      {enforceBudgetDialogOpenForSubaccount && (
        <EnforceBudgetDialog
          subaccount={enforceBudgetDialogOpenForSubaccount}
          onClose={() => setEnforceBudgetDialogOpenForSubaccount(null)}
        />
      )}
      {editBudgetDialogOpenForSubaccount && (
        <EditBudgetDialog
          subaccount={editBudgetDialogOpenForSubaccount.subaccount}
          budget={editBudgetDialogOpenForSubaccount.budget}
          onClose={() => setEditBudgetDialogOpenForSubaccount(null)}
        />
      )}
      {resetBudgetDialogOpenForSubaccount && (
        <ResetBudgetDialog
          subaccount={resetBudgetDialogOpenForSubaccount}
          onClose={() => setResetBudgetDialogOpenForSubaccount(null)}
        />
      )}
      {budgetHistoryDialogOpenForSubaccount && (
        <BudgetHistoryDialog
          subaccount={budgetHistoryDialogOpenForSubaccount.subaccount}
          latestBudget={budgetHistoryDialogOpenForSubaccount.budget}
          onClose={() => setBudgetHistoryDialogOpenForSubaccount(null)}
        />
      )}
    </PageContainer>
  );
}
